/**
 * ShareDialog - Dialog for sharing critique sessions
 * Feature: 080-critique-sharing
 * Tasks: T024, T055
 *
 * User Story 1: Copy Shareable Link
 * User Story 3: Share with Existing 206mp User
 *
 * Displays a modal with:
 * - Direct shareable link (copy to clipboard)
 * - Recipient search and selection for sharing with users
 * - Results view after sharing
 */

import { useState, useMemo, useCallback } from 'react';
import { Modal } from '@/components/composed/Modal';
import { CopyLinkRow } from './CopyLinkRow';
import { RecipientInput } from './RecipientInput';
import { ShareResultsView } from './ShareResultsView';
import { Button } from '@/components/atoms/Button';
import { critiqueUrl } from '@/utils/urls';
import {
  useShareCritiqueGraphQL,
  ShareRecipient,
  ShareCritiqueResult,
  ShareRecipientInput,
} from '@/hooks/graphql';
import { useDevFlags } from '@/contexts/DevFlagsContext';
import logger from '@/utils/logger';

export interface ShareDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The critique session ID to share */
  critiqueSessionId: string;
  /** Collection slug for building the full URL */
  collectionSlug?: string;
  /** Title to display in the dialog header */
  title?: string;
}

type DialogState = 'input' | 'sharing' | 'results';

/**
 * Share dialog for critique sessions
 *
 * Usage:
 *   const [shareDialogOpen, setShareDialogOpen] = useState(false);
 *
 *   <ShareDialog
 *     open={shareDialogOpen}
 *     onClose={() => setShareDialogOpen(false)}
 *     critiqueSessionId={session.globalId}
 *     collectionSlug={collection.slug}
 *     title={session.title}
 *   />
 */
export function ShareDialog({
  open,
  onClose,
  critiqueSessionId,
  collectionSlug,
  title,
}: ShareDialogProps) {
  const [dialogState, setDialogState] = useState<DialogState>('input');
  const [selectedRecipients, setSelectedRecipients] = useState<ShareRecipient[]>([]);
  const [shareResults, setShareResults] = useState<ShareCritiqueResult | null>(null);

  const { shareCritique, loading: sharing } = useShareCritiqueGraphQL();
  const devFlags = useDevFlags();
  const debugMode = devFlags?.flags.debugUI ?? false;

  // Build the full shareable URL
  const shareUrl = useMemo(() => {
    const path = critiqueUrl(critiqueSessionId, collectionSlug);
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}${path}`;
  }, [critiqueSessionId, collectionSlug]);

  const dialogTitle = title ? `Share "${title}"` : 'Share Discussion';

  // Reset state when dialog closes
  const handleClose = useCallback(() => {
    setDialogState('input');
    setSelectedRecipients([]);
    setShareResults(null);
    onClose();
  }, [onClose]);

  // Handle share button click
  const handleShare = useCallback(async () => {
    if (selectedRecipients.length === 0) return;

    setDialogState('sharing');

    try {
      // Convert ShareRecipient to ShareRecipientInput
      const recipients: ShareRecipientInput[] = selectedRecipients.map((r) => ({
        userId: r.userId || undefined,
        email: r.email || undefined,
      }));

      const result = await shareCritique(critiqueSessionId, recipients);

      if (result?.success) {
        logger.info('Sharing', 'Share completed', {
          critiqueSessionId,
          success: result.success,
          successCount: result.successCount,
        });
        // In debug mode, show results so we can see/copy invite links for testing
        if (debugMode) {
          setShareResults(result);
          setDialogState('results');
        } else {
          // Normal mode - just close the dialog (no confirmation needed)
          handleClose();
        }
      } else if (result) {
        // Partial failure - show results so user knows what failed
        setShareResults(result);
        setDialogState('results');
        logger.info('Sharing', 'Share partially failed', {
          critiqueSessionId,
          success: result.success,
          successCount: result.successCount,
          failureCount: result.failureCount,
        });
      } else {
        // Mutation returned null - show error
        setShareResults({
          success: false,
          message: 'Failed to share critique session',
          totalRecipients: recipients.length,
          successCount: 0,
          failureCount: recipients.length,
          results: recipients.map((r) => ({
            recipientId: r.userId || null,
            recipientEmail: r.email || null,
            success: false,
            message: 'Share failed',
            shareEventId: null,
            shareUrl: null,
            emailStatus: 'skipped' as const,
          })),
        });
        setDialogState('results');
      }
    } catch (err) {
      logger.error('Sharing', 'Share failed', { error: err });
      setDialogState('input');
    }
  }, [selectedRecipients, shareCritique, critiqueSessionId, debugMode, handleClose]);

  // Handle results dismissed
  const handleResultsDismiss = useCallback(() => {
    setDialogState('input');
    setSelectedRecipients([]);
    setShareResults(null);
  }, []);

  return (
    <Modal open={open} onClose={handleClose} title={dialogTitle} size="md">
      {/* Results view */}
      {dialogState === 'results' && shareResults && (
        <ShareResultsView results={shareResults} onDismiss={handleResultsDismiss} />
      )}

      {/* Input/Sharing view */}
      {(dialogState === 'input' || dialogState === 'sharing') && (
        <>
          <div className="space-y-6">
            {/* Share with users section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Share with People</h3>
              <p className="text-xs text-muted-foreground">
                Search for 206mp users by username, or enter an email to invite someone new.
              </p>
              <RecipientInput
                selectedRecipients={selectedRecipients}
                onRecipientsChange={setSelectedRecipients}
                disabled={dialogState === 'sharing'}
              />
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>

            {/* Share link section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Copy Link</h3>
              <p className="text-xs text-muted-foreground">
                Anyone with a 206mp account can view using this link.
              </p>
              <CopyLinkRow shareUrl={shareUrl} />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="secondary" onClick={handleClose} disabled={sharing}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleShare}
              disabled={selectedRecipients.length === 0 || sharing}
            >
              {sharing ? 'Sharing...' : `Share with ${selectedRecipients.length || ''} ${selectedRecipients.length === 1 ? 'person' : 'people'}`}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

// Re-export for convenience
export { CopyLinkRow } from './CopyLinkRow';
